// imports
import React, { Suspense, lazy, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Routes, Route, useLocation } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
// components
import UnsupportedBrowser from 'app/UnsupportedBrowser';
import { ApplyBranding, Footer, Navbar, PageLoader, RequireSession } from 'layout';
import { Button } from 'ui';
import MobileAlias from './MobileAlias';
import MobileOnlyFlow from './MobileOnlyFlow';
import GovtIdProviderGuard from 'features/govtId/v2/components/GovtIdProviderGuard';
import { ConnectionBanner } from 'features/connectivity';
// feature routes
import { OnboardingRoutes } from 'features/onboarding';
import { SessionExpiredRoute, NotFoundRoute } from 'features/common';
// assets
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChrome, faSafari } from '@fortawesome/free-brands-svg-icons';
import { DEFAULT_BRAND_COLORS, DEFAULT_BRAND_STYLES, REAUTHENTICATE_ROUTE } from 'utils/constants';
import { ROUTES } from 'utils/constants/routes';
import { FOOTER_HIDDEN_ROUTES, NAVBAR_HIDDEN_ROUTES } from 'helper/constant';
import { useGetProfileQuery } from 'api/profile';
import { useAnalyticsIdentity } from 'hooks/useAnalyticsIdentity';
import { useGovtIdV2Journey } from 'features/govtId/v2/analytics/hooks/useGovtIdV2Journey';
import { selectProfileLoading, selectCompany } from 'store/common/selectors/profile';

const GovtIdFlow = lazy(() => import('features/govtId').then((m) => ({ default: m.GovtIdFlow })));
const GovtIdV2Flow = lazy(() =>
    import('features/govtId/v2').then((m) => ({ default: m.GovtIdV2Flow }))
);
const Home = lazy(() => import('features/home').then((m) => ({ default: m.HomeRoutes })));

const inIframe = () => {
    try {
        return globalThis.self !== globalThis.top;
    } catch {
        // Cross-origin frames throw on `.top` access — that itself means we're framed.
        return true;
    }
};

const ContentArea = ({ reauthenticateSessionStatus, ...props }) => {
    const [unsupportedBrowser, setUnsupportedBrowser] = useState(false);
    const { pathname } = useLocation();

    const profileLoading = useSelector(selectProfileLoading);
    const company = useSelector(selectCompany);

    // TODO (Phase 8): remove — see import note above.
    // `undefined` = no arg (getProfile query ignores it); options must be 2nd param so undefined is required placeholder.
    useGetProfileQuery(undefined, { skip: !props?.session?.token });

    useAnalyticsIdentity();
    useGovtIdV2Journey();

    let primary = DEFAULT_BRAND_COLORS.primary;
    let secondary = DEFAULT_BRAND_COLORS.secondary;
    let overlayColor = DEFAULT_BRAND_COLORS.overlayColor;
    let modalBackgroundColor = DEFAULT_BRAND_COLORS.modalBackgroundColor;
    let modalBorderRadius = DEFAULT_BRAND_STYLES.modalBorderRadius;
    let fontFamily = DEFAULT_BRAND_STYLES.fontFamily;
    let buttonBorderRadius = DEFAULT_BRAND_STYLES.buttonBorderRadius;
    let navbarColor = DEFAULT_BRAND_COLORS.navbarColor;
    let headerColor = DEFAULT_BRAND_COLORS.headerColor;
    let stepperBackgroundColor = DEFAULT_BRAND_COLORS.stepperBackgroundColor;

    const isReAuthenticateRoute = pathname?.includes(REAUTHENTICATE_ROUTE);
    const reauthenticateTheme = reauthenticateSessionStatus?.theme;
    const isMedallionLoading = company && !profileLoading && company?.theme;
    const isThemeLoading = isMedallionLoading || isReAuthenticateRoute;
    const globalTheme = company?.theme || reauthenticateTheme;

    if (isThemeLoading) {
        primary = globalTheme?.primaryColor || DEFAULT_BRAND_COLORS.primary;
        secondary = globalTheme?.secondaryColor || DEFAULT_BRAND_COLORS.secondary;
        overlayColor = globalTheme?.overlayColor || DEFAULT_BRAND_COLORS.overlayColor;
        modalBackgroundColor =
            globalTheme?.modalBackgroundColor || DEFAULT_BRAND_COLORS.modalBackgroundColor;
        modalBorderRadius =
            globalTheme?.modalBorderRadius || DEFAULT_BRAND_STYLES.modalBorderRadius;
        fontFamily = globalTheme?.fontFamily || DEFAULT_BRAND_STYLES.fontFamily;
        buttonBorderRadius =
            globalTheme?.button?.borderRadius || DEFAULT_BRAND_STYLES.buttonBorderRadius;
        navbarColor = globalTheme?.navbarColor || DEFAULT_BRAND_COLORS.navbarColor;
        headerColor = globalTheme?.headerColor || DEFAULT_BRAND_COLORS.headerColor;
        stepperBackgroundColor =
            globalTheme?.stepperBgColor || DEFAULT_BRAND_COLORS.stepperBackgroundColor;
    }

    const applyColors = {
        primary: primary,
        secondary: secondary,
        overlayColor: overlayColor,
        modalBackgroundColor: modalBackgroundColor,
        navbarColor: navbarColor,
        headerColor: headerColor,
        stepperBackgroundColor: stepperBackgroundColor,
    };

    const isSupportScreen = pathname?.split('/')?.includes('need-support');

    // logic to hider navbar or footer based on particular screen where we need to render full size content
    const hideFooter = FOOTER_HIDDEN_ROUTES.includes(pathname);
    const hideNavbar = NAVBAR_HIDDEN_ROUTES.includes(pathname);

    const sessionGuardProps = {
        session: props.session,
        updateSessionToken: props.updateSessionToken,
        updateReauthenticateSessionID: props.updateReauthenticateSessionID,
    };

    return (
        <div>
            <ApplyBranding
                colors={applyColors}
                modalBorderRadius={modalBorderRadius}
                fontFamily={fontFamily}
                buttonBorderRadius={buttonBorderRadius}
            />
            <ConnectionBanner />
            {unsupportedBrowser ? (
                <UnsupportedBrowser
                    image="browsers.svg"
                    heading={
                        <div>
                            <p>We're sorry, but your browser isn't fully supported.</p>
                            <hr />
                            <p>
                                <b>Currently supported browsers</b>
                            </p>
                            <p>
                                <a
                                    href="https://www.google.com/chrome/"
                                    style={{ marginRight: '1rem' }}
                                >
                                    <FontAwesomeIcon icon={faChrome} size="2x" />
                                </a>
                                <a href="https://www.apple.com/in/safari/">
                                    <FontAwesomeIcon icon={faSafari} size="2x" />
                                </a>
                            </p>
                        </div>
                    }
                >
                    <Card.Footer className="text-muted">
                        <Button
                            block
                            variant="primary"
                            onClick={() => {
                                setUnsupportedBrowser(false);
                            }}
                        >
                            Continue Anyway
                        </Button>
                    </Card.Footer>
                </UnsupportedBrowser>
            ) : (
                <Suspense fallback={<PageLoader />}>
                    {!inIframe() && !isSupportScreen && !hideNavbar && <Navbar />}

                    <Routes>
                        {OnboardingRoutes}
                        {SessionExpiredRoute}
                        <Route
                            path={`${ROUTES.GOVT_ID_V2}/*`}
                            element={
                                <MobileOnlyFlow {...sessionGuardProps}>
                                    <GovtIdV2Flow />
                                </MobileOnlyFlow>
                            }
                        />
                        <Route
                            path="/govtid/*"
                            element={
                                <MobileOnlyFlow {...sessionGuardProps}>
                                    <GovtIdProviderGuard>
                                        <GovtIdFlow />
                                    </GovtIdProviderGuard>
                                </MobileOnlyFlow>
                            }
                        />
                        <Route path="/mobile/*" element={<MobileAlias />} />
                        <Route
                            path="/*"
                            element={
                                <RequireSession
                                    session={props.session}
                                    updateSessionToken={props.updateSessionToken}
                                    updateReauthenticateSessionID={
                                        props.updateReauthenticateSessionID
                                    }
                                    // passed this prop for reauthenticate session only 👇
                                    reauthenticateSession={props.reAuthenticateSessionID}
                                >
                                    <Home />
                                </RequireSession>
                            }
                        />
                        {NotFoundRoute}
                    </Routes>
                </Suspense>
            )}

            {!hideFooter && !inIframe() && <Footer />}
        </div>
    );
};

ContentArea.propTypes = {
    reauthenticateSessionStatus: PropTypes.object,
    reAuthenticateSessionID: PropTypes.string,
    session: PropTypes.any,
};

export default ContentArea;
